import React from "react";
import { Presentation, Users, Trophy, FileText } from "lucide-react";
import { PageHero, Reveal, Stagger, StaggerItem, Overline, SectionHeading, CTAButton, ArrowLink } from "@/components/primitives";
import { TagGrid, SponsorProposalBand } from "@/components/sections";
import { WHO_ATTENDS } from "@/data/site";

const HIGHLIGHTS = [
  { icon: Presentation, t: "5 business sessions", d: "Across the full agenda." },
  { icon: Users, t: "CEO roundtable", d: "On financing infrastructure." },
  { icon: Trophy, t: "Leadership Awards & gala", d: "The industry honours its own." },
  { icon: FileText, t: "Vision 2030 Declaration", d: "To close the forum." },
];

const THEMES = [
  ["Post-E20 growth", "Where demand goes once E20 is the floor."],
  ["Feedstock revolution", "Sugar, grain, maize, rice and 2G, and how they balance."],
  ["Flex-fuel & OMC demand", "Vehicles, blending economics, the price signal."],
  ["Global ethanol trade", "India as importer turned potential exporter."],
  ["Beyond ethanol", "SAF, bio-methanol and compressed biogas."],
];

export default function Conference() {
  return (
    <>
      <PageHero
        overline="The conference"
        title="Two days on the decisions that shape India's fuel."
        subtitle="Five business sessions, a CEO roundtable, the leadership awards and the India Ethanol Vision 2030 Declaration."
        image="/assets/site/conference-stage-panel.jpg"
        imageFallback="https://images.unsplash.com/photo-1587825140708-dfaf72ae4b04?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920"
      >
        <div className="flex flex-wrap gap-4">
          <CTAButton to="/conference/agenda">View the agenda</CTAButton>
          <CTAButton to="/conference/speakers" variant="outline" className="text-white">See the speakers</CTAButton>
        </div>
      </PageHero>

      {/* INTRO — why we're convening it */}
      <section className="bg-parchment py-20 md:py-28">
        <div className="mx-auto max-w-4xl px-6 md:px-12">
          <Reveal>
            <Overline className="mb-4">Why we're convening it</Overline>
            <h2 className="font-display text-3xl font-semibold tracking-tight text-navy sm:text-4xl">The questions that don't fit in a press release.</h2>
            <div className="mt-6 space-y-5 text-lg leading-relaxed text-navy/70">
              <p>India's ethanol story moved faster than almost anyone predicted — from 1.5% blending to E20 in a decade. But the next chapter is harder and less certain: E30 and flex-fuel vehicles, the right feedstock mix, the economics of higher blends, and the move into aviation fuel and biogas. Those questions need a room, not a webinar.</p>
              <p>Bharat Ethanol Forum is that room. Over two days, the people who actually make these decisions sit together and work them through. As a first edition we're keeping it deliberately focused — senior, candid, and built for real conversation rather than scale.</p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="border-y border-border bg-white py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <SectionHeading overline="Conference highlights" title="Built around decisions, not theory." />
          <Stagger className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {HIGHLIGHTS.map((h) => (
              <StaggerItem key={h.t}>
                <div className="flex h-full flex-col rounded-2xl border border-border bg-white p-7 shadow-sm transition-all hover:-translate-y-1 hover:shadow-xl">
                  <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-emerald2/10 text-emeraldDeep"><h.icon className="h-6 w-6" /></span>
                  <h3 className="mt-5 font-display text-lg font-bold text-navy">{h.t}</h3>
                  <p className="mt-2 text-sm text-navy/60">{h.d}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* THE ROOM — who shares it with you */}
      <section className="bg-parchment py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <Reveal>
            <Overline className="mb-4">The room</Overline>
            <h2 className="max-w-3xl font-display text-3xl font-semibold tracking-tight text-navy sm:text-4xl">A senior, deliberately small first edition.</h2>
            <p className="mt-6 max-w-3xl text-lg leading-relaxed text-navy/70">
              For the inaugural forum we're prioritising depth over numbers — a focused gathering of the people who set direction in India's ethanol economy. Expect leadership from across the value chain, candid conversation, and the kind of introductions that are hard to engineer anywhere else.
            </p>
          </Reveal>
          <Reveal className="mt-8"><TagGrid tags={WHO_ATTENDS} /></Reveal>
          <Reveal className="mt-8"><ArrowLink to="/attend">See why people attend</ArrowLink></Reveal>
        </div>
      </section>

      <section className="border-y border-border bg-white py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <SectionHeading overline="What's on the table" title="The questions this industry needs answered." />
          <div className="mt-12 divide-y divide-border border-y border-border">
            {THEMES.map(([t, d], i) => (
              <Reveal key={t} delay={i * 0.05}>
                <div className="grid items-center gap-2 py-6 md:grid-cols-12">
                  <p className="font-display text-lg font-bold text-navy md:col-span-4">{t}</p>
                  <p className="text-navy/60 md:col-span-8">{d}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-10"><ArrowLink to="/conference/agenda">View the full agenda</ArrowLink></Reveal>
        </div>
      </section>

      <SponsorProposalBand title="Your brand, on the stage where India's fuel policy is discussed." />

      <section className="relative overflow-hidden bg-[#0A1B33] grain py-20 text-center">
        <div className="relative mx-auto max-w-3xl px-6">
          <Reveal>
            <h2 className="font-display text-3xl font-semibold tracking-tight text-white sm:text-4xl">Be part of the conversation.</h2>
            <div className="mt-8 flex flex-wrap justify-center gap-4">
              <CTAButton to="/register">Register Now</CTAButton>
              <CTAButton to="/conference/speakers" variant="outline" className="text-white">See the speakers</CTAButton>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
